import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../Modal/Modal";
import Select from "../Select/Select";
import FileUploader from "../FileUploader/FileUploader";

type ImportModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onImport: (data: any) => void;
  fieldTypes?: Array<{ label: string; value: string }>;
  children?: React.ReactNode;
  confirmBtnLabel?: string;
  enableSubmit?: boolean;
  selectedType?: string;
  setSelectedType?: (value: string) => void;
  onDownloadExample?: () => void;
};

function ImportModal({
  isOpen,
  onClose,
  onImport,
  fieldTypes = [],
  children,
  confirmBtnLabel,
  enableSubmit,
  selectedType,
  setSelectedType,
  onDownloadExample,
}: ImportModalProps) {
  const { t } = useTranslation();

  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);

  const handleImport = () => {
    if (uploadedFiles.length) {
      if (fieldTypes.length > 0) {
        if (selectedType) {
          onImport({ files: uploadedFiles });
          onClose();
        } else {
          alert("Veuillez sélectionner un type.");
        }
      } else {
        onImport({ files: uploadedFiles });
        onClose();
      }
    } else {
      alert("Veuillez importer au moins un fichier.");
    }
  };

  const disableImport = useMemo(() => {
    if (fieldTypes.length > 0) {
      return !(
        selectedType !== undefined &&
        uploadedFiles.length > 0 &&
        enableSubmit
      );
    }
    return !(uploadedFiles.length > 0 && enableSubmit);
  }, [fieldTypes, selectedType, uploadedFiles, enableSubmit]);

  return (
    <Modal
      visible={isOpen}
      onCloseModal={onClose}
      onConfirmModal={handleImport}
      title={t("import.importModalTitle")}
      confirmBtnLabel={confirmBtnLabel ?? t("import.createBtn")}
      disableConfirmBtn={disableImport}
    >
      <div className="flex flex-col gap-4">
        {/* Sélecteur du type de champ */}
        {fieldTypes.length > 0 && (
          <div className="flex flex-col gap-2 items-start">
            <span className="text-m font-semibold">
              {t("forms.importTypeSelect")}
            </span>
            <Select
              options={fieldTypes}
              value={selectedType}
              onChange={setSelectedType}
              placeholder={t("forms.typeSelect")}
            />
          </div>
        )}

        {/* Champs supplémentaires */}
        {children && <div className="flex flex-col gap-4">{children}</div>}

        {/* Uploader de fichiers */}
        <FileUploader
          value={uploadedFiles}
          onChange={setUploadedFiles}
          accept=".xlsx"
          maxSize={5 * 1024 * 1024} // Limite à 5 Mo
          maxFiles={1}
        />

        {onDownloadExample && (
          <button
            type="button"
            className="w-full inline-flex justify-center rounded-md border enabled:border-transparent enabled:border-yellow-600 shadow-sm px-4 py-2 disabled:bg-gray-50 text-base font-medium enabled:text-yellow-600 enabled:hover:bg-yellow-600 enabled:hover:text-white  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 sm:col-start-1 sm:text-sm"
            onClick={onDownloadExample}
          >
            {t("export.downloadExample")}
          </button>
        )}
      </div>
    </Modal>
  );
}

export default ImportModal;
